<template>
  <div class="max-w-2xl px-4 mx-auto sm:px-6 lg:px-8">
    <div class="block pb-10 space-y-16">
      <!-- Content review status card -->
      <div>
        <h3 class="mb-3 text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Content Review</h3>
        <div class="overflow-hidden rounded-lg sm:shadow-md">
          <!-- Content -->
          <div class="flex items-start p-4 space-x-4 bg-white dark:bg-gray-900 sm:p-6">
            <CheckCircleIcon class="w-10 h-10 text-green-500 sm:h-12 sm:w-12" />
            <div class="space-y-4 text-gray-900 dark:text-white">
              <div>You're all set. No content review due right now.</div>
              <div>
                <router-link
                  to="/progress"
                  class="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-slate-100 px-4 py-2.5 text-base font-medium text-gray-800 shadow hover:bg-slate-200 dark:bg-gray-700 dark:text-white hover:dark:bg-gray-600 sm:w-auto"
                >
                  Check your progress
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Usage tips -->
      <div>
        <!-- Header -->
        <h3 class="mb-3 text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">
          <span v-if="userIsNew">Start saving content from anywhere</span>
          <span v-else>Choose how you want to save content</span>
        </h3>

        <!-- Content -->
        <div v-if="channels.length" class="block space-y-3">
          <template v-for="(channel, index) in channels" :key="index">
            <div class="relative p-4 overflow-hidden bg-white rounded-lg dark:border-none dark:bg-gray-900 dark:shadow-pink-500/60 sm:p-6 sm:shadow-lg">
              <div class="flex items-start pb-2 mb-2 space-x-4">
                <v-icon :name="channel.icon.name" scale="1" class="h-10 w-10 flex-shrink-0 rounded-full bg-slate-500 p-1.5 text-white sm:h-12 sm:w-12" />
                <div class="w-full">
                  <h4 class="text-lg font-medium text-gray-900 capitalize dark:text-white">{{ channel.name }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ channel.description }}</p>
                  <div class="flex items-center mt-1 space-x-2">
                    <div class="flex items-center font-medium rounded">
                      <span class="inline-flex items-center space-x-2 text-gray-800 dark:text-gray-100">
                        <DesktopComputerIcon
                          v-if="channel.desktop"
                          class="inline-flex h-5 w-5 items-center rounded bg-gray-100 px-0.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                        />
                        <DeviceMobileIcon
                          v-if="channel.mobile"
                          class="inline-flex h-5 w-5 items-center rounded bg-gray-100 px-0.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                        />
                      </span>
                    </div>
                    <div v-if="channel.recommended">
                      <span class="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"> Recommended </span>
                    </div>
                  </div>
                  <div class="mt-5">
                    <a
                      v-if="!channel.connected"
                      :href="channel.url"
                      class="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-slate-100 px-4 py-2.5 text-base font-medium text-gray-800 shadow hover:bg-slate-200 dark:bg-gray-700 dark:text-white hover:dark:bg-gray-600 sm:w-auto"
                    >
                      <div class="flex items-center">
                        <div>{{ channel.cta }}</div>
                      </div>
                    </a>

                    <div v-else class="flex space-x-2 text-green-500 item-center dark:text-green-500">
                      <CheckIcon class="w-6 h-6" />
                      <span>Connected</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import chromeExtension from "../composables/chromeExtension";
import { CheckCircleIcon, CheckIcon, DesktopComputerIcon, DeviceMobileIcon } from "@heroicons/vue/outline";
import { ref } from "@vue/reactivity";
import utilFunctions from "../composables/util";
import { watchEffect } from "@vue/runtime-core";
import { collection, getDocs } from "@firebase/firestore";
import { db } from "../firebase";

export default {
  components: {
    CheckCircleIcon,
    CheckIcon,
    DesktopComputerIcon,
    DeviceMobileIcon,
  },
  props: ["uid"],
  setup(props) {
    const { getUser, userInitials, extensionInstalled } = utilFunctions();
    const uid = ref(props.uid);
    const channels = ref([]);
    const userIsNew = ref(false);
    const userData = ref(null);

    const extensionIsInstalled = ref(false);
    extensionInstalled(chromeExtension.id, chromeExtension.image, (installed) => {
      extensionIsInstalled.value = installed;
    });

    watchEffect(async () => {
      userData.value = await getUser(uid.value);

      channels.value.push(
        {
          name: "WhatsApp",
          description: "Share and save any link to Wisery's official WhatsApp account.",
          icon: {
            name: "bi-whatsapp",
            color: "#25D366",
          },
          url: `https://wa.me/+18317039919?text=/start%20${uid.value}`,
          cta: "Connect WhatsApp",
          recommended: true,
          connected: userData.value.channels?.whatsapp?.id || null,
          mobile: true,
          desktop: false,
        },
        {
          name: "Chrome browser extension",
          description: "Made for desktop browsing. Save any content from the web by highlighting specific text or simply saving an entire article or page.",
          icon: {
            name: "si-googlechrome",
            color: "#4285F4",
          },
          url: chromeExtension.url,
          cta: "Install Extension",
          recommended: true,
          connected: userData.value.channels?.chrome?.id || extensionIsInstalled.value || null,
          mobile: false,
          desktop: true,
        },
        {
          name: "SMS",
          description: "Share and save any link by SMS.",
          icon: {
            name: "fa-sms",
            color: "#00C853",
          },
          url: `sms:+18317039919&body=/start%20${uid.value}`,
          cta: "Connect SMS",
          recommended: false,
          connected: userData.value.channels?.sms?.id || null,
          mobile: true,
          desktop: false,
        },
        {
          name: "Document Upload",
          description: "Upload any text based document. 7 different file formats supported.",
          icon: {
            name: "hi-document-add",
            color: "#F44336",
          },
          url: "/upload",
          cta: "Upload Document",
          recommended: false,
          connected: null,
          mobile: true,
          desktop: true,
        },
        {
          name: "Save Text",
          description: "Copy & paste or write text.",
          icon: {
            name: "hi-document-text",
            color: "#F44336",
          },
          url: "/upload",
          cta: "Submit Text",
          recommended: false,
          connected: null,
          mobile: true,
          desktop: true,
        }
      );

      // check if user is new by evaluating if user has reviews
      const reviewsRef = collection(db, "users", uid.value, "reviews");
      const reviewsSnap = await getDocs(reviewsRef);
      if (!reviewsSnap.empty) userIsNew.value = false;
    });

    return {
      userData,
      channels,
      userIsNew,
      userInitials,
    };
  },
};
</script>
